import { Component, Input, OnInit } from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { Store } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import DOMPurify from "dompurify";
import { ConfirmationDialogComponent } from "../../../shared/confirmation-dialog/confirmation-dialog.component";
import { Disclaimer } from "../disclaimers.interface";
import {
  ChangeDisclaimerAction,
  RemoveDisclaimerAction,
} from "../disclaimers.actions";
import { StartCheckingForInstallationInProgressAction } from "../../../signature-installation-state/signature-installation-state.actions";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../../shared/error-state-matcher";

@Component({
  selector: "app-disclaimer",
  templateUrl: "./disclaimer.component.html",
  styleUrls: ["./disclaimer.component.scss"],
  standalone: false,
})
export class DisclaimerComponent implements OnInit {
  @Input() disclaimer: Disclaimer;
  form: UntypedFormGroup;
  editMode = false;
  safeHtml: SafeHtml;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.fillInitialForm();
    this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(
      DOMPurify.sanitize(this.disclaimer.htmlContent ?? "", {
        ALLOWED_TAGS: [
          "a",
          "b",
          "br",
          "em",
          "i",
          "img",
          "p",
          "span",
          "strong",
          "table",
          "td",
          "tr",
          "div",
          "ul",
          "ol",
          "li",
        ],
      }),
    );
  }

  fillInitialForm = () => {
    this.form = new UntypedFormGroup({
      addonId: new UntypedFormControl(this.disclaimer.addonId),
      name: new UntypedFormControl(this.disclaimer.name, (c) =>
        Validators.required(c),
      ),
      htmlContent: new UntypedFormControl(this.disclaimer.htmlContent, (c) =>
        Validators.required(c),
      ),
    });
  };

  changeDisclaimer = () => {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.invalid) {
      return;
    }
    this.store.dispatch(new StartCheckingForInstallationInProgressAction());
    const formValue = this.form.value as {
      addonId: string;
      name: string;
      htmlContent: string;
    };
    this.store.dispatch(new ChangeDisclaimerAction(formValue));
    this.changeEditMode();
  };

  cancelEditDisclaimer = () => {
    this.fillInitialForm();
    this.changeEditMode();
  };

  changeEditMode = () => {
    this.editMode = !this.editMode;
    if (!this.editMode) {
      this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(
        DOMPurify.sanitize(this.disclaimer.htmlContent ?? "", {
          ALLOWED_TAGS: [
            "a",
            "b",
            "br",
            "em",
            "i",
            "img",
            "p",
            "span",
            "strong",
            "table",
            "td",
            "tr",
            "div",
            "ul",
            "ol",
            "li",
          ],
        }),
      );
    }
  };

  removeDisclaimer = (addonDisclaimerId: string) => {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translate.instant(
            "addons.disclaimers.deleteConfirmationText",
          ) as string,
        },
      })
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.store.dispatch(
            new StartCheckingForInstallationInProgressAction(),
          );
          this.store.dispatch(new RemoveDisclaimerAction(addonDisclaimerId));
        }
      });
  };
}
